import React, { useState, useEffect } from "react";
import { FaUndo, FaTrash } from "react-icons/fa";
import { Table, Modal, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  getDeactiveUserPages,
  UpdateDeactiveUsers,
  getDeactiveUsers,
  permanentlyDeleteUser,
} from "../Redux/Actions/Useraction";
import Paginationcomponent from "./pagination";
import notify from "../Hook/useNotification";
import { useTranslation } from "react-i18next";

const AllDeactiveUsers = ({ keyword }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [show, setShow] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [actionType, setActionType] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const handleClose = () => {
    setShow(false);
    setSelectedId(null);
    setActionType(null);
  };

  const handleShowRestore = (id) => {
    setSelectedId(id);
    setActionType("restore");
    setShow(true);
  };

  const handleShowDelete = (id) => {
    setSelectedId(id);
    setActionType("delete");
    setShow(true);
  };

  useEffect(() => {
    dispatch(getDeactiveUsers(10, keyword));
  }, [keyword, dispatch]);

  const decativeUserData = useSelector((state) => state.alluser.deactiveusers);
  const loading = useSelector((state) => state.alluser.loading);
  const allDecativeUser = decativeUserData?.data?.users || [];

  // Helper function to calculate duration dynamically
  const getDeactiveDuration = (updatedAt) => {
    if (!updatedAt) return "-";

    const diffInMs = new Date() - new Date(updatedAt);
    const diffInMins = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays > 0) {
      return `${diffInDays} ${diffInDays === 1 ? t("day", "day") : t("days", "days")}`;
    }
    if (diffInHours > 0) {
      return `${diffInHours} ${diffInHours === 1 ? t("hour", "hour") : t("hours", "hours")}`;
    }
    if (diffInMins > 0) {
      return `${diffInMins} ${diffInMins === 1 ? t("minute", "minute") : t("minutes", "minutes")}`;
    }
    return t("just_now", "just now");
  };

  const handleConfirm = async () => {
    setActionLoading(true);
    let res;

    try {
      if (actionType === "restore") {
        res = await dispatch(UpdateDeactiveUsers(selectedId));

        // Checks API success criteria safely
        if (
          res &&
          (res.status === 200 ||
            res.data?.status === "success" ||
            res.status === "success")
        ) {
          notify(t("doctorTab.notify.success"), "success");
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          notify(t("doctorTab.notify.error"), "error");
        }
      } else if (actionType === "delete") {
        res = await dispatch(permanentlyDeleteUser(selectedId));

        if (
          res &&
          (res.status === 200 ||
            res.status === 204 ||
            res.data?.status === "success" ||
            res.status === "success")
        ) {
          notify(t("doctorTab.notify.delete_permanent_success"), "success");
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          notify(t("doctorTab.notify.delete_permanent_error"), "error");
        }
      }
    } catch (error) {
      console.error("Action error:", error);
      notify(t("doctorTab.notify.delete_permanent_error"), "error");
    } finally {
      setActionLoading(false);
      handleClose();
    }
  };

  let pagecount = 0;
  if (decativeUserData?.pagination?.numberOfPages) {
    pagecount = decativeUserData?.pagination?.numberOfPages;
  }

  const getpage = (page) => {
    dispatch(getDeactiveUserPages(page));
  };

  return (
    <>
      {loading ? (
        <p>{t("doctorTab.loading")}</p>
      ) : allDecativeUser.length === 0 ? (
        <p>{t("doctorTab.no_doctors")}</p>
      ) : (
        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th>{t("doctorTab.table.name")}</th>
              <th>{t("doctorTab.table.email")}</th>
              <th>{t("doctorTab.table.role")}</th>
              <th>{t("doctorTab.table.duration", "Deactivated For")}</th>{" "}
              {/* ← New Header Column */}
              <th>{t("doctorTab.table.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {allDecativeUser.map((doctor, index) => (
              <tr key={index}>
                <td>{doctor.firstName + " " + doctor.lastName}</td>
                <td>{doctor.email}</td>
                <td>{doctor.role}</td>
                <td>
                  <span className="badge bg-secondary">
                    {getDeactiveDuration(doctor.updatedAt)}{" "}
                    {/* ← Duration calculation tag */}
                  </span>
                </td>
                <td>
                  <FaUndo
                    style={{
                      cursor: "pointer",
                      color: "#28a745",
                      marginRight: "12px",
                    }}
                    title={t("doctorTab.modal.restore")}
                    onClick={() => handleShowRestore(doctor._id)}
                  />
                  <FaTrash
                    style={{ cursor: "pointer", color: "#dc3545" }}
                    title={t("doctorTab.modal.delete_permanent")}
                    onClick={() => handleShowDelete(doctor._id)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {pagecount > 1 && (
        <Paginationcomponent pagecount={pagecount} onpress={getpage} />
      )}

      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            {actionType === "restore"
              ? t("doctorTab.modal.restore_title")
              : t("doctorTab.modal.delete_permanent_title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {actionType === "restore"
            ? t("doctorTab.modal.restore_body")
            : t("doctorTab.modal.delete_permanent_body")}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={actionLoading}
          >
            {t("doctorTab.modal.cancel")}
          </Button>
          <Button
            variant={actionType === "restore" ? "success" : "danger"}
            onClick={handleConfirm}
            disabled={actionLoading}
          >
            {actionLoading
              ? "..."
              : actionType === "restore"
                ? t("doctorTab.modal.restore")
                : t("doctorTab.modal.confirm_delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AllDeactiveUsers;
